import { useContext, useMemo } from 'react';
import type { MouseEvent } from 'react';
import { navigate } from 'gatsby';
import { PageMetadataContext } from '../templates/PageMetaDataContext';
import { prefixWithLang } from '../global/countries';

const normalizePath = (path: string) => path.replace(/\/+$/, '') || '/';

// Delegate CMS-authored anchors without changing their server-rendered hrefs.
export function navigateSiteLink(
    event: MouseEvent<HTMLElement>,
    paths: Set<string>
): boolean {
    if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
    )
        return false;
    const anchor =
        event.target instanceof Element
            ? event.target.closest('a[href]')
            : null;
    if (
        !(anchor instanceof HTMLAnchorElement) ||
        !event.currentTarget.contains(anchor)
    )
        return false;
    if (
        anchor.hasAttribute('download') ||
        (anchor.target && anchor.target !== '_self') ||
        anchor.rel.split(/\s+/).includes('external')
    )
        return false;
    const href = anchor.getAttribute('href');
    if (!href || href.includes('#')) return false;
    const url = new URL(anchor.href);
    // Consent preferences are opened by the consent SDK's document-load handler.
    if (url.searchParams.has('ketch_show')) return false;
    if (
        url.origin !== window.location.origin ||
        !paths.has(normalizePath(url.pathname))
    )
        return false;
    event.preventDefault();
    void navigate(url.pathname + url.search);
    return true;
}

export default function useSiteNavigation(onNavigate?: () => void) {
    const { pagePaths, preferredLanguage } = useContext(PageMetadataContext);
    const paths = useMemo(
        () =>
            new Set(
                Object.values(pagePaths || {}).flatMap((path) => [
                    normalizePath(path),
                    normalizePath(
                        prefixWithLang(path, preferredLanguage || 'default')
                    ),
                ])
            ),
        [pagePaths, preferredLanguage]
    );
    return (event: MouseEvent<HTMLElement>) => {
        if (navigateSiteLink(event, paths)) onNavigate?.();
    };
}
